/**
 * Responsive image component with lazy loading
 * Provides automatic srcset generation, lazy loading, and responsive breakpoints
 */

import { globalState } from '@/components/helpers/utils'
import { ComponentProps } from 'preact'
import { useEffect, useState } from 'preact/hooks'

interface PreactImageProps extends ComponentProps<'image'> {
  imageUrl: string[] | string
  alt: string
  className: string
  imageClass: string
  isLazy: boolean
  isCover: boolean
  imageSrcSet: string[]
  imageBreakpoints: string
  imageSizes: number[]
}

function PreactImage({
  imageUrl = globalState.shopify.defaultImage,
  alt,
  className,
  imageClass,
  isLazy = true,
  isCover = true,
  imageSrcSet = [],
  imageBreakpoints = '(min-width: 0px)',
  imageSizes = [150]
}: PreactImageProps) {
  const [currentImageUrls] = useState(imageUrl)
  const classImage = `${isCover ? 'object-cover' : 'object-contain'} ${isLazy && 'lazy'} ${imageClass}`
  const customClass = `relative ${className}`
  let fallbackImageUrl = currentImageUrls[0]
  let breakpoints = imageBreakpoints.includes('|') ? imageBreakpoints.split('|') : [imageBreakpoints]

  if (currentImageUrls.length <= 1) {
    breakpoints = breakpoints.splice(0, 1)
  } else {
    breakpoints = breakpoints.splice(0, currentImageUrls.length)
    fallbackImageUrl = currentImageUrls[currentImageUrls.length - 1]
  }

  const sizes = imageSizes.map((curr, index) => `${breakpoints[index]} ${curr}vw`).join(',')

  useEffect(() => {
    if (window.lazyLoadInstance) {
      window.lazyLoadInstance.update()
    }
  }, [])

  const getSrcSet = (url: string) => {
    return imageSrcSet.length > 0 ? imageSrcSet.map((srcset) => `${url} ${srcset}w `).join(',') : url
  }

  return (
    <picture className={customClass}>
      {breakpoints.map((breakpoint, index) => (
        <source
          className="!size-0"
          media={breakpoint}
          data-srcset={getSrcSet(currentImageUrls[index])}
          data-size={sizes}
        />
      ))}
      <img
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUCB1j+Pjx438ACX0D04PrXz0AAAAASUVORK5CYII="
        data-src={fallbackImageUrl}
        className={classImage}
        alt={alt || 'Image'}
      />
    </picture>
  )
}

export default PreactImage
